import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
	extend, reverse, sortBy, groupBy
} from 'lodash';
import {
	branch,
	compose,
	withStateHandlers,
	withState,
	withPropsOnChange,
	renderNothing,
	lifecycle,
	withHandlers
} from 'recompose';

import withViewportWidth from 'decorators/withViewportWidth';

import { GoogleMap } from 'components/core/library/mapV2';
import { ZoomControls } from 'components/core/Map';
import GoogleMaps from 'lib/googleMaps';
import { isLarge } from 'utils/breakpoints';
import { locationsToBounds } from 'utils/locationsToBounds';
import toLatLng from 'utils/toLatLng';

import FacilityMarkers from './FacilityMarkers';
import SearchOnMoveControl from './SearchOnMoveControl';

export const withGoogleOnMount = compose(
	withState('google', 'setGoogle', null),
	lifecycle({
		componentDidMount() {
			const { google } = GoogleMaps.getInstance({ key: this.props.googleMapsKey });
			google.then(this.props.setGoogle);
		}
	}),
	branch(({ google }) => !google, renderNothing)
);

export const withMapFacilities = compose(
	withViewportWidth,
	withPropsOnChange(['facilities'], (props) => {
		let facilities = props.facilities.map(
			(facility) => extend(facility, { location: toLatLng(facility.location) })
		);

		facilities = groupBy(
			reverse(
				sortBy(facilities.filter((f) => f.inNetwork), 'location.lat')
			),
			((f) => (f.isSelfStorage && 'storage'))
		);

		return {
			facilities: facilities.storage || []
		};
	}),
	withStateHandlers(({ initialZoom, searchOnMove }) => ({
		currentZoom: initialZoom,
		mapMoved: false,
		loading: true,
		showRedoButton: false,
		searchOnMove,
		mapBounds: null
	}), {
		zoomIn: ({ currentZoom }) => () => ({ currentZoom: currentZoom + 1 }),
		zoomOut: ({ currentZoom }) => () => ({ currentZoom: currentZoom - 1 }),
		handleZoomChange: () => (currentZoom) => ({ currentZoom }),
		handleIdle: (_, { onReady }) => () => { onReady(); return { loading: false }; },
		toggleSearchOnMove: ({ searchOnMove }) => () => ({ searchOnMove: !searchOnMove }),
		setMapBounds: () => (mapBounds) => ({ mapBounds }),
		setRedoButton: () => (showRedoButton) => ({ showRedoButton }),
		handleRedoSearch: ({ mapBounds }, { onRedoSearch }) => () => {
			onRedoSearch(mapBounds);
			return { showRedoButton: false, searchOnMove: true };
		}
	}),
	withPropsOnChange(['google', 'facilities', 'searchLocation'],
		({
			google, facilities, searchLocation, preselectedFacilityId
		}) => {
			const preselectedFacility = facilities.filter(({ id }) => id === preselectedFacilityId);
			if (google && facilities.length) {
				// Map Polygons if only has FSSd
				const initialBounds = locationsToBounds(
					google,
					preselectedFacility.length ? preselectedFacility : facilities
				);
				// If I have a pre-selected facility it will center on it
				// If not I will center within the collection of facilities
				const centerObj = (preselectedFacilityId && preselectedFacility.length) ?
					locationsToBounds(google, preselectedFacility).getCenter() : initialBounds.getCenter();
				const center = { lat: centerObj.lat(), lng: centerObj.lng() };

				return { initialBounds, center };
			}
			return { center: searchLocation };
		}),
	// Override search on move on smaller screens
	withPropsOnChange(
		['breakpoint', 'searchOnMove'],
		({ viewportWidth, searchOnMove }) => ({ searchOnMove: isLarge(viewportWidth) ? searchOnMove : false })
	),
	withHandlers({
		handleBoundsChange: ({
			setMapBounds, setRedoButton, searchOnMove, onRedoSearch
		}) => (bounds) => {
			if (searchOnMove) {
				onRedoSearch(bounds);
			}

			setMapBounds(bounds);
			setRedoButton(!searchOnMove);
		}
	})
);

export function Map({
	google, center, currentZoom, handleZoomChange, handleBoundsChange, handleIdle, facilities, breakpoint,
	loading, zoomIn, zoomOut, showRedoButton, searchOnMove, toggleSearchOnMove, handleRedoSearch, initialBounds,
	phone, highlightedFacility, getLocalPhoneNumberByZipCode,
	preselectedFacilityId, showControls = true
}) {
	return (
		<div className="map-container-v2">
			<GoogleMap
				center={center}
				initialBounds={initialBounds}
				zoom={currentZoom}
				onZoomChanged={handleZoomChange}
				onBoundsChange={handleBoundsChange}
				onIdle={handleIdle}
				google={google}
				preselectedFacilityId={preselectedFacilityId}
			>
				<FacilityMarkers
					facilities={facilities}
					highlightedFacility={highlightedFacility}
					breakpoint={breakpoint}
					phone={phone}
					getLocalPhoneNumberByZipCode={getLocalPhoneNumberByZipCode}
					preselectedFacilityId={preselectedFacilityId}
				/>
			</GoogleMap>
			{showControls && <div className={classnames('map-control-container-v2', { loading })}>
				<div className="search-on-move-box">
					<SearchOnMoveControl
						showRedoButton={showRedoButton}
						searchOnMove={searchOnMove}
						onToggleSearchOnMove={toggleSearchOnMove}
						onRedoSearch={handleRedoSearch}
					/>
				</div>
				<ZoomControls
					onZoomIn={zoomIn}
					onZoomOut={zoomOut}
				/>
			</div>}
		</div>
	);
}

Map.propTypes = {
	zoomIn: PropTypes.func,
	zoomOut: PropTypes.func,
	handleZoomChange: PropTypes.func,
	handleBoundsChange: PropTypes.func,
	center: PropTypes.shape({
		lat: PropTypes.number,
		lng: PropTypes.number
	}),
	currentZoom: PropTypes.number,
	handleIdle: PropTypes.func,
	facilities: PropTypes.arrayOf(PropTypes.object),
	loading: PropTypes.bool,
	showRedoButton: PropTypes.bool,
	searchOnMove: PropTypes.bool,
	toggleSearchOnMove: PropTypes.func,
	handleRedoSearch: PropTypes.func,
	initialBounds: PropTypes.object,
	google: PropTypes.object,
	breakpoint: PropTypes.string,
	phone: PropTypes.object,
	getLocalPhoneNumberByZipCode: PropTypes.func,
	highlightedFacility: PropTypes.number,
	preselectedFacilityId: PropTypes.number
};

export const enhance = compose(
	withGoogleOnMount,
	withMapFacilities
);

export default enhance(Map);
